import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// constellation
import { Anchor, Heading, Paragraph } from '@zillow/constellation';

// components
import UnsubscribeError from 'app/shared/components/UnsubscribeError';

// actions
import { updateSubscription } from 'app/shared/flux/actions/MarketingActions';

// misc/utils
import { augmentError, formatForLog } from 'app/shared/utils/errors';
import { queryStringToQueryObject } from 'app/shared/utils/urlUtils';
import * as S from './styles';

// types
import type { State } from 'app/shared/types/State';
import type { MarketUpdateStatusErrorType, MarketUpdateStatusType } from 'app/shared/types/ApiResponseTypes';

const logger = getLogger('pages/UnsubscribePage');

const UnsubscribePage = () => {
  const queryString = useSelector((state: State) => state.router.location.search);
  const pathname = useSelector((state: State) => state.router.location.pathname);
  const unsubscribeResult = useSelector((state: State) => state.marketing.unsubscribeResult);
  const dispatch = useDispatch();
  const queryObj = queryStringToQueryObject(queryString);
  const { email, marketUpdateId } = queryObj;
  const [isSubscribed, setIsSubscribed] = useState(unsubscribeResult.success ? unsubscribeResult.isSubscribed : null);
  const [address, setAddress] = useState(unsubscribeResult.success ? unsubscribeResult.address : null);
  const [isErrored, setIsErrored] = useState(!unsubscribeResult.success);
  const handleUndoClick = () => {
    dispatch(updateSubscription({ email, enabled: !isSubscribed, marketUpdateId }))
      // @ts-ignore
      .then((res: MarketUpdateStatusType) => {
        const { marketUpdateEnabled, streetAddress } = res;
        const { street, state, city, zip, unit, hasUnitLabel } = streetAddress;
        const formattedAddress: string = `${street} ${hasUnitLabel ? unit : ''}, ${city}, ${state} ${zip}`;
        setIsSubscribed(marketUpdateEnabled);
        setAddress(formattedAddress);
        return;
      })
      .catch((err: MarketUpdateStatusErrorType) => {
        augmentError(err, {
          root: 'pages/UnsubscribePage#handleUndoClick',
          pathAttempted: pathname,
          queryAttempted: queryString,
        });
        logger.error(formatForLog(err), 'Unsubscribe page: Error updating subscription');
        setIsErrored(true);
      });
  };
  return (
    <S.Page>
      <S.Content>
        {isErrored ? (
          <UnsubscribeError />
        ) : (
          <>
            <Heading level={2} as="h1" marginTop="lg" fontFamily="serif">
              Thank you!
            </Heading>
            <Paragraph as="h3" marginTop="md">
              You have been {isSubscribed ? 'subscribed to' : 'unsubscribed from'} the Rental Marketplace Update.
            </Paragraph>
            <Paragraph fontType="body" marginTop="sm">
              Property: <strong>{address}</strong>
            </Paragraph>
            <Paragraph fontType="body" marginTop="sm">
              Email address: <strong>{email}</strong>
            </Paragraph>
            <Paragraph fontType="body" marginTop="sm">
              <Anchor onClick={handleUndoClick}>
                <strong>Undo</strong>
              </Anchor>
            </Paragraph>
          </>
        )}
      </S.Content>
    </S.Page>
  );
};

export default UnsubscribePage;
